import { CampaignLog } from "@bt-common/schemas/bt/campaign-log";
import { CampaignTimelineItem } from "./CampaignTimelineItem";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import moment from "moment";
import { formatDelta } from "@common/utils/time-format-utils";

interface CampaignLogTimelineProps {
    logs: CampaignLog[];
    campaignId?: string;
}

// formatDelta is now imported from @common/utils/time-format-utils

function formatGroupMetadata(group: CampaignLog[], previousGroupLastLog?: CampaignLog): { timeRange?: string; duration?: string; eventCount: number; deltaFromPrevious?: string } {
    if (group.length === 0) {
        return { eventCount: 0 };
    }

    const firstLog = group[0];
    const lastLog = group[group.length - 1];
    
    const firstTime = moment(firstLog.createdAt);
    const lastTime = moment(lastLog.createdAt);
    
    const timeRange = group.length > 1 
        ? `${firstTime.format('h:mm:ss A')} - ${lastTime.format('h:mm:ss A')}`
        : firstTime.format('h:mm:ss A');
    
    const durationMs = lastTime.valueOf() - firstTime.valueOf();
    const duration = durationMs > 0 
        ? moment.duration(durationMs).humanize()
        : undefined;

    const deltaFromPrevious = previousGroupLastLog 
        ? formatDelta(previousGroupLastLog.createdAt!, firstLog.createdAt!)
        : undefined;

    return {
        timeRange,
        duration,
        eventCount: group.length,
        deltaFromPrevious
    };
}

function groupLogsByTimeProximity(logs: CampaignLog[], thresholdMs: number = 10000): CampaignLog[][] {
    if (logs.length === 0) {
        return [];
    }

    // Sort logs chronologically (oldest first)
    const sortedLogs = [...logs].sort((a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf());

    const groups: CampaignLog[][] = [];
    let currentGroup: CampaignLog[] = [sortedLogs[0]];

    for (let i = 1; i < sortedLogs.length; i++) {
        const currentLog = sortedLogs[i];
        const lastLogInGroup = currentGroup[currentGroup.length - 1];
        
        const currentTime = moment(currentLog.createdAt).valueOf();
        const lastTimeInGroup = moment(lastLogInGroup.createdAt).valueOf();
        const timeDifference = currentTime - lastTimeInGroup;

        if (timeDifference <= thresholdMs) {
            currentGroup.push(currentLog);
        } else {
            groups.push(currentGroup);
            currentGroup = [currentLog];
        }
    }

    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }

    return groups;
}

export function CampaignLogTimeline({ logs, campaignId }: CampaignLogTimelineProps) {
    if (!logs || logs.length === 0) {
        return (
            <Card className="border-slate-200 dark:border-slate-700">
                <CardContent className="py-4">
                    <p className="text-sm text-slate-500 dark:text-slate-400">No log entries available</p>
                </CardContent>
            </Card>
        );
    }

    const logGroups = groupLogsByTimeProximity(logs);

    return (
        <div className="space-y-4">
            {logGroups.map((group, groupIndex) => {
                const previousGroupLastLog = groupIndex > 0 && logGroups[groupIndex - 1].length > 0
                    ? logGroups[groupIndex - 1][logGroups[groupIndex - 1].length - 1]
                    : undefined;
                
                const metadata = formatGroupMetadata(group, previousGroupLastLog);
                
                return (
                    <Card 
                        key={`group-${groupIndex}`}
                        className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 shadow-sm"
                    >
                        <CardContent className="p-4">
                            {/* Group header with metadata */}
                            <div className="mb-3 pb-2 border-b border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    {metadata.timeRange && (
                                        <span>{metadata.timeRange}</span>
                                    )}
                                    {metadata.duration && (
                                        <>
                                            <span className="text-slate-400 dark:text-slate-500">•</span>
                                            <span>{metadata.duration}</span>
                                        </>
                                    )}
                                    <span className="text-slate-400 dark:text-slate-500">•</span>
                                    <span>{metadata.eventCount} event{metadata.eventCount !== 1 ? 's' : ''}</span>
                                    {metadata.deltaFromPrevious && groupIndex > 0 && (
                                        <>
                                            <span className="text-slate-400 dark:text-slate-500">•</span>
                                            <Badge 
                                                variant="outline" 
                                                className="text-xs font-normal bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 px-2 py-0.5"
                                            >
                                                {metadata.deltaFromPrevious}
                                            </Badge>
                                        </>
                                    )}
                                </div>
                            </div>
                            
                            {/* Timeline items within group */}
                            <div className="space-y-1">
                                {group.map((log, logIndex) => {
                                    const previousLogTimestamp = logIndex > 0 ? group[logIndex - 1].createdAt : undefined;
                                    const isLastInGroup = logIndex === group.length - 1;
                                    const isFirstInGroup = logIndex === 0;
                                    
                                    return (
                                        <CampaignTimelineItem
                                            key={log._id || `${campaignId}-${groupIndex}-${logIndex}`}
                                            log={log}
                                            isLast={isLastInGroup}
                                            isFirst={isFirstInGroup}
                                            previousLogTimestamp={previousLogTimestamp}
                                        />
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}
